import { inject, Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { finalize, Observable, switchMap, tap } from "rxjs";
import { ShortCache } from "../../shared/contract/Cache/ShortCache";
import { InitService } from "src/app/shared/services/init.service";
import { CapitalizedIncomeValueService } from "../capitalized-income-value/capitalized-income-value.service";
import { Router } from "@angular/router";
import { LoadingService } from "src/app/modules/loading/loading.service";
import { AccumateModule, MODULE_NAMES } from "src/app/shared/consts/icon-features";
import { MenuService } from "src/app/modules/menu/menu.service";
import { EstateType } from "src/app/shared/contract/Enum/portfolio.enum";
import { Coordinate, ObjectCoordinate } from "src/app/shared/contract/Address/Coordinates";
import { EPSGType } from "src/app/shared/contract/Enum/EPSG";
import { BasemapService } from "../basemap/basemap.service";
import { PlotResponse } from "src/app/shared/contract/Plot/PlotResponse";
import { PlotService } from "../plot/services/plot.service";
import { SpatialRequest } from "../../shared/contract/SpatialAnalysis/SpatialRequest";
import { SpatialAnalysisService } from "../spatial-analysis/spatial-analysis.service";
import { SpatialResponse } from "../../shared/contract/SpatialAnalysis/SpatialResponse";
import { DrawService } from "@services/draw.service";
import { RealEstateStoreService } from "../real-estate/services/real-estate-store.service";
import { RealEstateBaseObject } from "../real-estate/models/real-estate-base-object.interface";
import { ModuleEnum } from "src/app/shared/contract/Enum/ModuleEnum";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";
import { getCoordinateFromObjectCoordinate } from "src/app/shared/helpers/coordinate-helper";
import { MapViewService } from "@services/map-view.service";
import { MixedRealEstateService } from "../real-estate/inherit-real-estate-service/mixed-real-estate.service";
import { MixedUnitDetailStatus } from "src/app/shared/models/mixed-unit-detail-status.enum";

@Injectable({
  providedIn: "root"
})
export class CacheService {
  private readonly httpClient = inject(HttpClient);
  private readonly initService = inject(InitService);
  private readonly capitalizedIncomeValueService = inject(CapitalizedIncomeValueService);
  private readonly router = inject(Router);
  private readonly loadingService = inject(LoadingService);
  private readonly menuService = inject(MenuService);
  private readonly baseMapService = inject(BasemapService);
  private readonly plotService = inject(PlotService);
  private readonly drawService = inject(DrawService);
  private readonly thematicInfoService = inject(ThematicInfoService);
  private readonly spatialAnalysisService = inject(SpatialAnalysisService);
  private readonly realEstateStoreService = inject(RealEstateStoreService);
  private readonly mixedRealEstateService = inject(MixedRealEstateService);
  private readonly mapViewService = inject(MapViewService);

  private readonly cacheBasePath = "/api/Cache";
  private readonly cacheOverviewURL = `${this.cacheBasePath}/ShortCacheList`;
  private readonly cacheResponseURL = `${this.cacheBasePath}/CacheResponse`;
  private readonly cacheRequestURL = `${this.cacheBasePath}/CacheRequest`;
  private readonly existsCacheURL = `${this.cacheBasePath}/ExistsCache`;

  loadCacheOverview(): Observable<ShortCache[]> {
    return this.httpClient.get<ShortCache[]>(this.initService.getApiUrl(this.cacheOverviewURL));
  }

  loadCacheResponseData(id: number) {
    const params = new HttpParams().set("cacheId", id.toString());
    return this.httpClient.get<RealEstateBaseObject | PlotResponse | SpatialResponse>(this.initService.getApiUrl(this.cacheResponseURL), {
      params
    });
  }

  loadCacheRequestData(id: number) {
    const params = new HttpParams().set("cacheId", id.toString());
    return this.httpClient.get(this.initService.getApiUrl(this.cacheRequestURL), { params });
  }

  existsCache(addressId: number, estateType: EstateType): Observable<ShortCache | null> {
    const params = new HttpParams().set("addressId", addressId?.toString()).set("estateType", estateType.toString());
    return this.httpClient.get<ShortCache | null>(this.initService.getApiUrl(this.existsCacheURL), { params });
  }

  openPlotFromCache(cacheId: number) {
    return this.loadCacheResponseData(cacheId).pipe(
      tap((cacheResponseData: PlotResponse) => {
        this.plotService.plotResponse.set(cacheResponseData);
        this.plotService.hasPlotCalculationChanged.set(false);
        this.setCurrentSelectedMarkerFromCachePayload(cacheResponseData);
      }),
      switchMap(() => this.menuService.setMode(MODULE_NAMES.BuildingLand))
    );
  }

  openObjectFromCache(cacheId: number, module: ModuleEnum): Observable<boolean> {
    this.loadingService.show();

    return this.loadCacheResponseData(cacheId).pipe(
      switchMap(realEstateData => {
        let accumateModule: AccumateModule;

        switch (module) {
          case ModuleEnum.Rvm:
            accumateModule = MODULE_NAMES.Rvm;
            break;
          case ModuleEnum.BPS:
            accumateModule = MODULE_NAMES.BuildingLand;
            this.plotService.plotResponse.set(realEstateData as PlotResponse);
            this.plotService.hasPlotCalculationChanged.set(false);
            break;
          case ModuleEnum.APS:
            accumateModule = MODULE_NAMES.ResidentialRealEstate;
            break;
          case ModuleEnum.CommercialAVM:
            this.thematicInfoService.isRent.set(true);
            accumateModule = MODULE_NAMES.CommercialRealEstate;
            break;
          case ModuleEnum.MixedRealEstate:
            accumateModule = MODULE_NAMES.MixedRealEstate;
            break;
        }

        if (
          module === ModuleEnum.CommercialAVM ||
          module === ModuleEnum.APS ||
          module === ModuleEnum.Rvm ||
          module === ModuleEnum.MixedRealEstate
        ) {
          if (module === ModuleEnum.MixedRealEstate) {
            const normalizedMixedData = this.normalizeMixedRealEstateCacheData(realEstateData as RealEstateBaseObject);
            this.mixedRealEstateService.setMixedRealEstateAndSubscribeToUnitsStream(normalizedMixedData, true);
          } else {
            this.realEstateStoreService.setRealEstate(realEstateData as RealEstateBaseObject, accumateModule);
          }

          if (module === ModuleEnum.Rvm) {
            const coordinate = this.getCoordinateFromCachePayload(realEstateData);
            if (coordinate) {
              const transformedCoordinate = getCoordinateFromObjectCoordinate(coordinate);
              this.mapViewService.setViewByCoordinates(transformedCoordinate.Get(EPSGType.OSM), 14);
            }
          } else if (module === ModuleEnum.CommercialAVM || module === ModuleEnum.APS) {
            this.realEstateStoreService.setValueFixing();
          }

          if (module === ModuleEnum.CommercialAVM) {
            this.capitalizedIncomeValueService.createInternalCapitalizedIncomeValueData();
            this.capitalizedIncomeValueService.setCapitalizedIncomeValue();
          }
        }

        this.setCurrentSelectedMarkerFromCachePayload(realEstateData);

        return this.menuService.setMode(accumateModule);
      }),
      finalize(() => this.loadingService.hide())
    );
  }

  openRealEstateFromCache(cacheId: number, accumateModule: AccumateModule) {
    this.loadingService.show();
    return this.loadCacheResponseData(cacheId).pipe(
      tap((cacheResponseData: RealEstateBaseObject) => {
        this.capitalizedIncomeValueService.createInternalCapitalizedIncomeValueData();
        this.realEstateStoreService.setRealEstate(cacheResponseData, accumateModule);
        this.setCurrentSelectedMarkerFromCachePayload(cacheResponseData);

        if (accumateModule === MODULE_NAMES.CommercialRealEstate) {
          this.thematicInfoService.isRent.set(true);
          this.capitalizedIncomeValueService.setCapitalizedIncomeValue();
        }
      }),
      switchMap(() => this.menuService.setMode(accumateModule))
    );
  }

  navigateToSpatialAnalysisWithCache(cacheId: number, cacheRequestData: SpatialRequest) {
    this.loadingService.show();

    return this.loadCacheResponseData(cacheId).pipe(
      tap((cacheResponseData: SpatialResponse) => {
        this.spatialAnalysisService.spatialAnalysisData.set(cacheResponseData);
        this.drawService.countryFromDraw = cacheRequestData.country;
        this.spatialAnalysisService.drawCachedSpatialAnalysis(cacheRequestData);
      }),
      switchMap(() => this.router.navigate(["spatial-analysis"])),
      finalize(() => this.loadingService.hide())
    );
  }

  private normalizeMixedRealEstateCacheData(data: RealEstateBaseObject): RealEstateBaseObject {
    return {
      ...data,
      mixedRealEstateFacts: data.mixedRealEstateFacts
        ? {
            totalUnits: data.mixedRealEstateFacts.totalUnits,
            constructionYearFrom: data.mixedRealEstateFacts.constructionYearFrom,
            constructionYearTo: data.mixedRealEstateFacts.constructionYearTo,
            plotSize: data.mixedRealEstateFacts.plotSize,
            totalUnitArea: data.mixedRealEstateFacts.totalUnitArea
          }
        : null,
      units: (data.units ?? []).map(unit => ({
        technicalId: unit.technicalId,
        objectSubType: unit.objectSubType,
        totalUnits: unit.totalUnits,
        totalArea: unit.totalArea,
        constructionYear: unit.constructionYear,
        unitId: unit.unitId ?? "",
        mixedUnitDetailStatus: MixedUnitDetailStatus.Ready,
        mixedUnitDetailErrorMessage: null,
        mixedUnitDetailAvailableAtUtc: null
      }))
    };
  }

  private setCurrentSelectedMarker(coordinates: ObjectCoordinate) {
    this.baseMapService.currentSelectedMarkerCoordinate = new Coordinate([coordinates.longitude, coordinates.latitude], EPSGType.LonLat);
  }

  private setCurrentSelectedMarkerFromCachePayload(cachePayload: unknown): void {
    const coordinate = this.getCoordinateFromCachePayload(cachePayload);
    if (!coordinate) {
      return;
    }

    this.setCurrentSelectedMarker(coordinate);
  }

  private getCoordinateFromCachePayload(cachePayload: unknown): ObjectCoordinate | null {
    const payload = cachePayload as {
      address?: { coordinate?: ObjectCoordinate };
      Address?: { Coordinate?: { Country?: string | null; Latitude?: number | null; Longitude?: number | null } };
    };

    if (payload?.address?.coordinate) {
      return payload.address.coordinate;
    }

    const legacyCoordinate = payload?.Address?.Coordinate;
    if (!legacyCoordinate) {
      return null;
    }

    return {
      country: legacyCoordinate.Country,
      latitude: legacyCoordinate.Latitude,
      longitude: legacyCoordinate.Longitude
    };
  }
}
